import type { DetectionBox } from './Detector';

const HUE_BINS = 8;

export interface ShallowFeature {
  kind: 'shallow';
  hue: Float32Array;
  aspectRatio: number;
  brightness: number;
  sampleCount: number;
}

export interface DeepFeature {
  kind: 'deep';
  embedding: Float32Array;
  sampleCount: number;
}

export type ReIdFeature = ShallowFeature | DeepFeature;

export function emptyShallowFeature(): ShallowFeature {
  return {
    kind: 'shallow',
    hue: new Float32Array(HUE_BINS),
    aspectRatio: 0,
    brightness: 0,
    sampleCount: 0,
  };
}

export function rgbToHueBin(r: number, g: number, b: number): { bin: number; v: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  let h = 0;
  if (delta > 0) {
    if (max === rn) h = ((gn - bn) / delta) % 6;
    else if (max === gn) h = (bn - rn) / delta + 2;
    else h = (rn - gn) / delta + 4;
    h = (h * 60 + 360) % 360;
  }
  const bin = Math.min(HUE_BINS - 1, Math.floor((h / 360) * HUE_BINS));
  return { bin, v: max };
}

export function sampleFeature(
  video: HTMLVideoElement,
  box: DetectionBox,
  offscreen: HTMLCanvasElement | OffscreenCanvas,
): ShallowFeature | null {
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (w <= 0 || h <= 0) return null;
  const sx = Math.max(0, box.x * w);
  const sy = Math.max(0, box.y * h);
  const sw = Math.max(1, Math.min(w - sx, box.w * w));
  const sh = Math.max(1, Math.min(h - sy, box.h * h));
  const target = 16;
  offscreen.width = target;
  offscreen.height = target;
  const ctx = offscreen.getContext('2d') as
    | CanvasRenderingContext2D
    | OffscreenCanvasRenderingContext2D
    | null;
  if (!ctx) return null;
  try {
    ctx.clearRect(0, 0, target, target);
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, target, target);
    const data = ctx.getImageData(0, 0, target, target).data;
    const hue = new Float32Array(HUE_BINS);
    let brightness = 0;
    let n = 0;
    for (let i = 0; i < data.length; i += 4) {
      const bin = rgbToHueBin(data[i], data[i + 1], data[i + 2]);
      hue[bin.bin] += 1;
      brightness += bin.v;
      n += 1;
    }
    if (n === 0) return null;
    for (let i = 0; i < HUE_BINS; i += 1) hue[i] /= n;
    return {
      kind: 'shallow',
      hue,
      aspectRatio: box.h / Math.max(0.001, box.w),
      brightness: brightness / n,
      sampleCount: 1,
    };
  } catch {
    return null;
  }
}

function mergeShallow(a: ShallowFeature, b: ShallowFeature, maxSamples: number): ShallowFeature {
  const totalBefore = a.sampleCount;
  const totalAfter = Math.min(maxSamples, totalBefore + b.sampleCount);
  const w = totalBefore === 0 ? 1 : b.sampleCount / totalAfter;
  const keep = 1 - w;
  const hue = new Float32Array(HUE_BINS);
  for (let i = 0; i < HUE_BINS; i += 1) hue[i] = a.hue[i] * keep + b.hue[i] * w;
  return {
    kind: 'shallow',
    hue,
    aspectRatio: a.aspectRatio * keep + b.aspectRatio * w,
    brightness: a.brightness * keep + b.brightness * w,
    sampleCount: totalAfter,
  };
}

function mergeDeep(a: DeepFeature, b: DeepFeature, maxSamples: number): DeepFeature {
  const totalBefore = a.sampleCount;
  const totalAfter = Math.min(maxSamples, totalBefore + b.sampleCount);
  const w = totalBefore === 0 ? 1 : b.sampleCount / totalAfter;
  const keep = 1 - w;
  const len = Math.min(a.embedding.length, b.embedding.length);
  const out = new Float32Array(len);
  for (let i = 0; i < len; i += 1) out[i] = a.embedding[i] * keep + b.embedding[i] * w;
  let norm = 0;
  for (let i = 0; i < len; i += 1) norm += out[i] * out[i];
  norm = Math.sqrt(norm);
  if (norm > 0) for (let i = 0; i < len; i += 1) out[i] /= norm;
  return { kind: 'deep', embedding: out, sampleCount: totalAfter };
}

export function mergeFeature(a: ReIdFeature, b: ReIdFeature, maxSamples = 20): ReIdFeature {
  if (a.kind !== b.kind) return b;
  if (a.kind === 'deep' && b.kind === 'deep') return mergeDeep(a, b, maxSamples);
  if (a.kind === 'shallow' && b.kind === 'shallow') return mergeShallow(a, b, maxSamples);
  return b;
}

function shallowDistance(a: ShallowFeature, b: ShallowFeature): number {
  let hueDist = 0;
  for (let i = 0; i < HUE_BINS; i += 1) {
    const d = a.hue[i] - b.hue[i];
    hueDist += d * d;
  }
  hueDist = Math.sqrt(hueDist);
  const arDist =
    Math.abs(a.aspectRatio - b.aspectRatio) / Math.max(1, a.aspectRatio + b.aspectRatio);
  const brDist = Math.abs(a.brightness - b.brightness);
  return hueDist * 1.0 + arDist * 0.3 + brDist * 0.2;
}

export function cosineDistance(a: Float32Array, b: Float32Array): number {
  const len = Math.min(a.length, b.length);
  if (len === 0) return 1;
  let dot = 0;
  let na = 0;
  let nb = 0;
  for (let i = 0; i < len; i += 1) {
    dot += a[i] * b[i];
    na += a[i] * a[i];
    nb += b[i] * b[i];
  }
  if (na === 0 || nb === 0) return 1;
  return Math.max(0, 1 - dot / (Math.sqrt(na) * Math.sqrt(nb)));
}

export function featureDistance(a: ReIdFeature, b: ReIdFeature): number {
  if (a.kind !== b.kind) return Number.POSITIVE_INFINITY;
  if (a.kind === 'deep' && b.kind === 'deep') {
    return cosineDistance(a.embedding, b.embedding);
  }
  if (a.kind === 'shallow' && b.kind === 'shallow') {
    return shallowDistance(a, b);
  }
  return Number.POSITIVE_INFINITY;
}
